import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll-to-top on route change.
 *
 * Mounted once inside <Routes>. On every pathname change (and search-string
 * change for paginated catalog), it resets window scroll to the top so that
 * navigating from the bottom of one page lands at the top of the next.
 *
 * Skipped when:
 *   - The browser's history.scrollRestoration is doing the right thing (back/forward)
 *   - The new URL has a hash fragment (the browser will jump to the anchor itself)
 */
export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (hash) {
      // Honour anchor links — if the element exists, scroll to it. This
      // covers the case where the user is already on the page (e.g. clicking
      // "Categories" in the header from the home page itself, where React
      // Router doesn't re-mount but the URL hash changes).
      const id = hash.startsWith("#") ? hash.slice(1) : hash;
      // Defer one tick so any newly-rendered route can mount the target.
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return;
    }
    // `instant` avoids a jarring smooth-scroll on page change; the new page
    // should feel like it was already there.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // `key` is included so back/forward navigation with the same pathname
    // (rare, but possible) also resets scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, key, hash]);

  return null;
}
